import { z } from "zod";

/** Credential lifecycle states (mirrors `credentials.status`). */
export const CredentialStatus = z.enum(["issued", "claimed", "revoked"]);
export type CredentialStatus = z.infer<typeof CredentialStatus>;

/** RBAC roles on a `membership` (see ARCHITECTURE §5.1). */
export const MembershipRole = z.enum(["owner", "admin", "editor", "viewer"]);
export type MembershipRole = z.infer<typeof MembershipRole>;

/** A single recipient row for bulk issuance (CSV column contract, REQ-1.5). */
export const IssuanceRecipientSchema = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  issueDate: z.string().optional(),
  grade: z.string().optional(),
});
export type IssuanceRecipient = z.infer<typeof IssuanceRecipientSchema>;

/** Public verification result surfaced on the verify page (REQ-2.9). */
export const VerificationResultSchema = z.object({
  publicId: z.string(),
  status: CredentialStatus,
  signatureValid: z.boolean(),
  chainValid: z.boolean(),
  issuer: z.object({ name: z.string(), slug: z.string() }),
  recipientName: z.string().nullable(),
  issuedAt: z.string(),
  revokedAt: z.string().nullable(),
});
export type VerificationResult = z.infer<typeof VerificationResultSchema>;

/** Slug format shared by issuers and (later) CV/hosted URLs. */
export const SlugSchema = z
  .string()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, "lowercase letters, digits, and hyphens only");

/** POST /v1/issuers — create an issuer + generate its Ed25519 signing key. */
export const CreateIssuerRequestSchema = z.object({
  name: z.string().min(1),
  slug: SlugSchema,
});
export type CreateIssuerRequest = z.infer<typeof CreateIssuerRequestSchema>;

export const AchievementInputSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  criteria: z.string().optional(),
});

/** POST /v1/credentials — single issue (REQ-1.5 single path). */
export const CreateCredentialRequestSchema = z.object({
  recipient: z.object({
    name: z.string().min(1),
    email: z.string().email(),
  }),
  achievement: AchievementInputSchema,
  /** ISO-8601; defaults to now server-side when omitted. */
  issuedAt: z.string().datetime().optional(),
});
export type CreateCredentialRequest = z.infer<typeof CreateCredentialRequestSchema>;

/** POST /v1/issuers/:slug/issuance/bulk — CSV or JSON array (REQ-1.5). */
export const BulkIssueRequestSchema = z.object({
  groupId: z.string().uuid(),
  achievement: AchievementInputSchema,
  /** Provide recipients inline as JSON… */
  recipients: z.array(IssuanceRecipientSchema).optional(),
  /** …or as raw CSV text (Name, Email, Issue Date, Grade). */
  csv: z.string().optional(),
});
export type BulkIssueRequest = z.infer<typeof BulkIssueRequestSchema>;

/** One queue message = one recipient to issue (fanned out by the API). */
export interface BulkIssueMessage {
  jobId: string;
  issuerId: string;
  groupId: string;
  achievement: { name: string; description?: string; criteria?: string };
  recipient: IssuanceRecipient;
  baseUrl: string;
}
